import {
  all,
  take,
  takeEvery,
  takeLatest,
  put,
  select,
  call,
  race,
} from 'redux-saga/effects';
import {
  withOffset,
  selectors as streamSelectors,
} from '@wix/da-shared-react/pkg/Stream';
import {
  GROUP_ACTIONLOG_IDS_STREAM,
  GROUP_MESSAGELOG_IDS_STREAM,
  MESSAGES_PER_FETCH,
} from '../streams';
import {
  filterGroupActionLogByRole,
  sortGroupActionLog,
  filterGroupMessageLogByNotificationType,
  filterGroupMessageLogByRole,
  filterGroupMessageLogByStatus,
  sortGroupMessageLog,
  starNotes,
  unstarNotes,
  archiveNotes,
  unarchiveNotes,
} from '../actions/backroomSection';
import {
  fetchModuleEndpoint,
  fetchModuleEndpointError,
  fetchModuleEndpointSuccess,
} from '../actions/modules';
import { getProfileOwnerUser } from '../selectors/users';
import { ModuleType } from '../../types/modules';
import { getModuleIdByModuleType } from '../selectors/modules';

import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { getDenormalizedDataEntitiesByIds } from '@wix/da-shared-react/pkg/redux/entities/selectors';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { GroupNotesSchema, GroupNoteSchema } from '../schemas/modules';

import { getGroupNotesStreamId } from '../streams/groupnotes';
import { GroupNotesFolderType } from '../../types/backroomSection';

function* filterGroupActionLog(newStreamParams) {
  const streamParams = yield select(
    streamSelectors.getStreamParams,
    GROUP_ACTIONLOG_IDS_STREAM
  );
  yield put(
    withOffset.actions.initialize({
      streamId: GROUP_ACTIONLOG_IDS_STREAM,
      streamParams: {
        ...streamParams,
        ...newStreamParams,
      },
      itemsPerFetch: MESSAGES_PER_FETCH,
    })
  );
  yield put(withOffset.actions.fetch(GROUP_ACTIONLOG_IDS_STREAM));
}

function* handleFilterGroupActionLogByRole(
  action: ReturnType<typeof filterGroupActionLogByRole>
) {
  yield call(filterGroupActionLog, { roleId: action.payload.roleId });
}

function* handleSortGroupActionLog(
  action: ReturnType<typeof sortGroupActionLog>
) {
  yield call(filterGroupActionLog, { sortValue: action.payload.sortValue });
}

function* filterGroupMessageLog(newStreamParams) {
  const streamParams = yield select(
    streamSelectors.getStreamParams,
    GROUP_MESSAGELOG_IDS_STREAM
  );
  yield put(
    withOffset.actions.initialize({
      streamId: GROUP_MESSAGELOG_IDS_STREAM,
      streamParams: {
        ...streamParams,
        ...newStreamParams,
      },
      itemsPerFetch: MESSAGES_PER_FETCH,
    })
  );
  yield put(withOffset.actions.fetch(GROUP_MESSAGELOG_IDS_STREAM));
}

function* handleFilterMessageLogByNotificationType(
  action: ReturnType<typeof filterGroupMessageLogByNotificationType>
) {
  yield call(filterGroupMessageLog, {
    instigatorModuleType: action.payload.notificationType,
  });
}

function* handleFilterMessageLogByRole(
  action: ReturnType<typeof filterGroupMessageLogByRole>
) {
  yield call(filterGroupMessageLog, {
    instigatorRoleid: action.payload.roleId,
  });
}

function* handleFilterMessageLogByStatus(
  action: ReturnType<typeof filterGroupMessageLogByStatus>
) {
  yield call(filterGroupMessageLog, { status: action.payload.status });
}

function* handleSortMessageLog(action: ReturnType<typeof sortGroupMessageLog>) {
  yield call(filterGroupMessageLog, { sortBy: action.payload.sortBy });
}

function* tagNotes(
  noteids: number[],
  tag: 'star' | 'unstar' | 'archive' | 'unarchive'
) {
  const { username } = yield select(getProfileOwnerUser);
  const id = yield select(getModuleIdByModuleType, ModuleType.GROUP_NOTES);
  yield put(
    fetchModuleEndpoint({
      id,
      username,
      path: '/tag',
      method: 'post',

      noteids,
      action: tag,
    })
  );

  const { error } = yield race({
    success: take(fetchModuleEndpointSuccess),
    error: take(fetchModuleEndpointError),
  });

  if (error) {
    return false;
  }
  return true;
}

// update normalized entities in the store
function* updateGroupnotes(noteids, overrides = {}) {
  // Fetch the notes from the store
  let notes = yield select(
    getDenormalizedDataEntitiesByIds,
    GroupNoteSchema,
    noteids
  );
  notes = notes.map(note => ({ ...note, ...overrides }));
  const { entities } = normalize(notes, GroupNotesSchema);
  yield put(putEntities({ entities }));
}

function* handleStarNotes(action: ReturnType<typeof starNotes>) {
  const noteIds = action.payload.noteIds;
  const success = yield call(tagNotes, noteIds, 'star');
  if (success) {
    yield call(updateGroupnotes, noteIds, { isStarred: true });
  }
}

function* handleUnstarNotes(action: ReturnType<typeof unstarNotes>) {
  const noteIds = action.payload.noteIds;
  const success = yield call(tagNotes, noteIds, 'unstar');
  if (success) {
    yield call(updateGroupnotes, noteIds, { isStarred: false });
    // Remove these items from the starred stream
    yield put(
      withOffset.actions.deleteItems({
        streamId: getGroupNotesStreamId(GroupNotesFolderType.STARRED),
        items: noteIds,
      })
    );
  }
}

function* handleArchiveNotes(action: ReturnType<typeof archiveNotes>) {
  const noteIds = action.payload.noteIds;
  const success = yield call(tagNotes, noteIds, 'archive');
  if (success) {
    yield call(updateGroupnotes, noteIds, { isArchived: true });
    // Remove these items from the inbox stream
    yield put(
      withOffset.actions.deleteItems({
        streamId: getGroupNotesStreamId(GroupNotesFolderType.INBOX),
        items: noteIds,
      })
    );
  }
}

function* handleUnarchiveNotes(action: ReturnType<typeof unarchiveNotes>) {
  const noteIds = action.payload.noteIds;
  const success = yield call(tagNotes, noteIds, 'unarchive');
  if (success) {
    yield call(updateGroupnotes, noteIds, { isArchived: false });
    // Remove these items from the archived stream
    yield put(
      withOffset.actions.deleteItems({
        streamId: getGroupNotesStreamId(GroupNotesFolderType.ARCHIVED),
        items: noteIds,
      })
    );
  }
}

export default function* rootGroupsBackroomSaga() {
  yield all([
    takeEvery(filterGroupActionLogByRole, handleFilterGroupActionLogByRole),
    takeEvery(sortGroupActionLog, handleSortGroupActionLog),
    takeEvery(
      filterGroupMessageLogByNotificationType,
      handleFilterMessageLogByNotificationType
    ),
    takeEvery(filterGroupMessageLogByRole, handleFilterMessageLogByRole),
    takeEvery(filterGroupMessageLogByStatus, handleFilterMessageLogByStatus),
    takeEvery(sortGroupMessageLog, handleSortMessageLog),
    takeLatest(starNotes, handleStarNotes),
    takeLatest(unstarNotes, handleUnstarNotes),
    takeLatest(archiveNotes, handleArchiveNotes),
    takeLatest(unarchiveNotes, handleUnarchiveNotes),
  ]);
}
